<template>
  <div class="comments_sidebar">
    <div class="header">
      <span>{{ comments.length }} comments</span>
      <comments-settings-dropdown />
    </div>
    <div v-if="comments.length == 0" class="no_results_msg">
      No comments yet
    </div>
    <div class="results">
      <comments-section-item
        v-for="comment in comments"
        :id="comment.id"
        :key="comment.id"
        @clickEvent="onClickComment(comment)"
      />
    </div>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import emitter from 'tiny-emitter/instance';
import { EVENT } from '../../../constants.js';

import CommentsSectionItem from './CommentsSectionItem.vue';
import CommentsSettingsDropdown from './CommentsSettingsDropdown.vue';

export default {
  components: {
    CommentsSectionItem,
    CommentsSettingsDropdown,
  },

  data() {
    return {
      popoverOpen: false,
      search: null,
    };
  },
  computed: {
    ...mapGetters('project/data/comments', [
      'getAllThreads',
      'getThreadLastReplyDate',
      'getContainerNode',
    ]),
    ...mapGetters('project/config/comments', [
      'getShowResolved',
      'getAllowNotifications',
      'getIsSelected',
    ]),

    /**
     * Returns an array with all the threads in the project, sorted by last reply date descending order.
     * @returns {array}
     */
    comments() {
      const filterResolved = !this.getShowResolved;
      const threads = this.getAllThreads(filterResolved);
      return threads.sort((a, b) => {
        const dateA = this.getThreadLastReplyDate(a.id);
        const dateB = this.getThreadLastReplyDate(b.id);
        // Sort in descending order
        return new Date(dateB) - new Date(dateA);
      });
    },
  },

  methods: {
    ...mapActions('project/config/comments', [
      'setOpen',
      'setShowResolved',
      'setAllowNotifications',
      'deselectMany',
    ]),

    /**
     * Event handler for clicking a thread item.
     * @param   {Object}  element
     */
    onClickComment(comment) {
      const { x, y } = comment.position;
      const { id } = comment;
      const board = this.getContainerNode(id, 'boards', 'comments');
      console.log(x, y, id, board);
      emitter.emit(EVENT.ZOOMTONODE, {
        node: comment,
        type: 'comments',
        boardId: board.id,
      });
      this.setOpen({ id, apply: true });
    },
  },
};
</script>

<style scoped lang="scss">
.comments_sidebar {
  width: 295px;
  background: #2c3233;
  z-index: 2;
  box-shadow: -2px 3px 6px 0 rgba(0, 0, 0, 0.2);
  &.no_results {
    position: absolute;
    right: 0;
  }

  .header {
    color: #fff;
    opacity: 0.8;
    font-size: 13px;
    padding: 8px 10px;
    // background: #242929;
    border-bottom: 1px solid rgba(62, 71, 71, 0.75);
    display: flex;
    align-items: center;
    span {
      flex: 1;
    }
    :deep(.v-popover.active .trigger) {
      background: #242929;
    }
    :deep(.v-popover) {
      display: flex !important;
      // padding: 8px 8px;
      width: 28px;
      height: 28px;
      align-items: center;
      justify-content: center;

      .trigger {
        width: 28px;
        height: 28px;
        display: flex !important;
        align-items: center;
        justify-content: center;
        border-radius: 4px;
        cursor: pointer;
        .tooltip-target,
        .svg-icon-container {
          height: 100%;
        }
        &:hover {
          background: #242929;
        }
      }
    }
  }
  .no_results_msg {
    color: #bcbcbc;
    font-size: 14px;
    font-weight: 400;
    opacity: 0.8;
    display: flex;
    align-items: center;
    height: 100%;
    justify-content: center;
  }
  .results {
    height: calc(100% - 45px);
    overflow: auto;
    .item {
      display: flex;
      align-items: flex-start;
      justify-content: left;
      min-width: 30px;
      min-height: 30px;
      width: 100%;
      padding: 8px 10px 7px 10px;
      cursor: pointer;
      &:hover {
        background: #363e3f;
      }
      .icon {
        display: flex;
        align-items: center;
        justify-content: center;
        min-width: 16px;
        max-width: 16px;
        margin: 2px 9px 0 0;
      }
      .thumb {
        width: 22px;
        height: 22px;
        min-width: 22px;
        min-height: 22px;
        overflow: hidden;
        margin: 0 6px 0 -2px;
        display: flex;
        align-items: center;
        justify-content: center;
        &.hasimage {
          border-radius: 999px;
        }
        img {
          width: 100%;
          height: 100%;
          object-fit: cover;
          user-select: none;
          -webkit-user-drag: none;
        }
      }
      .details {
        flex: 1;
        display: flex;
        flex-flow: column;
        width: calc(100% - 15px);
        .name {
          display: block;
          color: #bcbcbc;
          overflow: hidden;
          text-overflow: ellipsis;
          font-size: 14px;
          font-weight: 600;
          margin: 0 0 4px 0;
          // flex: 1;

          :deep(span) {
            color: #fff;
          }
        }
        .content {
          color: #bcbcbc;
          flex: 1;
          margin: 0 0 3px 0;
          // font-style: italic;
          :deep(span) {
            color: #fff;
          }
        }
        .parent {
          flex: 0 0 100%;
          color: #bcbcbc;
          font-size: 12px;
          opacity: 0.7;
        }
      }
    }
  }
}

// SIDEBAR SCROLLBAR STYLES

.comments_sidebar .results {
  scrollbar-color: rgba(0, 0, 0, 0.5) transparent;
  scrollbar-width: thin;
  overflow: hidden;
  @media (hover: none) {
    overflow-y: auto;
  }
  &:hover {
    overflow-y: auto;
    overflow: overlay;
  }
}

.comments_sidebar .results::-webkit-scrollbar {
  width: 10px;
}

.comments_sidebar .results::-webkit-scrollbar-thumb {
  background: rgba(0, 0, 0, 0.5);
  border: 2px solid transparent;
  border-radius: 999px;
  background-clip: padding-box;
}

.comments_sidebar .results::-webkit-scrollbar-button {
  display: none;
}
</style>
